import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function flattenCriteria(categories = []) {
  return categories.flatMap((category) => category.criteria.flatMap((criterion) => (
    criterion.type === 'group' ? criterion.criteria : [criterion]
  )));
}

function ScoreField({ criterion, value, previousValue, allowNoStatement, onChange }) {
  const minimumScore = allowNoStatement ? 0 : 1;
  const marks = [
    ...(allowNoStatement ? [{ value: 0, label: 'Keine Aussage' }] : []),
    ...[1, 2, 3, 4].map((score) => ({ value: score, label: String(score) })),
  ];
  const sliderValue = value === null ? 0 : value;
  const previousValuePosition = ((previousValue - minimumScore) / (4 - minimumScore)) * 100;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, width: '100%', height: 168, boxSizing: 'border-box' }}>
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{criterion.label}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {previousValue != null && <Chip size="small" variant="outlined" label={`Bisher: ${previousValue}`} />}
            <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{criterion.infoText}</Box>} arrow>
              <IconButton size="small" aria-label={`Hinweis zu ${criterion.label}`}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ px: allowNoStatement ? { xs: 6, sm: 7 } : 1.5 }}>
          <Box sx={{ position: 'relative' }}>
            {previousValue != null && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: `calc(${previousValuePosition}% + ${12 - (previousValuePosition / 100) * 24}px)`,
                  top: 9,
                  width: 3,
                  height: 18,
                  bgcolor: 'text.secondary',
                  borderRadius: 1,
                  zIndex: 1,
                }}
              />
            )}
            <Slider
              min={minimumScore}
              max={4}
              step={1}
              marks={marks}
              value={Number.isInteger(sliderValue) ? sliderValue : minimumScore}
              onChange={(_, score) => onChange(score === 0 ? null : score)}
              valueLabelDisplay="auto"
              valueLabelFormat={(score) => score === 0 ? 'Keine Aussage' : score}
              getAriaValueText={(score) => score === 0 ? 'Keine Aussage' : String(score)}
              aria-label={criterion.label}
            />
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function SelfReflectionForm({
  config,
  initialValues = {},
  previousValues = {},
  allowNoStatement = false,
  saving = false,
  onSave,
  onSaved,
  onCancel,
  saveLabel = 'Bewertung speichern',
}) {
  const criteria = useMemo(() => flattenCriteria(config?.categories), [config]);
  const [values, setValues] = useState({});

  useEffect(() => {
    setValues({
      ...Object.fromEntries(criteria.map((criterion) => [criterion.key, allowNoStatement ? null : 1])),
      ...initialValues,
    });
  }, [allowNoStatement, criteria, initialValues]);

  const submitValues = Object.fromEntries(criteria.map((criterion) => [criterion.key, values[criterion.key]]));
  const canSave = Object.keys(submitValues).length > 0
    && Object.values(submitValues).every((value) => value === null || Number.isInteger(value));
  const handleSave = async () => {
    const saved = await onSave(submitValues);
    if (saved !== false) onSaved?.();
  };

  return (
    <Stack spacing={2}>
      {(config?.categories || []).map((category) => (
        <Stack key={category.key} spacing={1.25}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{category.label}</Typography>
          <Grid container spacing={1.25}>
            {category.criteria.map((criterion) => {
              if (criterion.type === 'group') {
                return (
                  <Grid size={12} key={criterion.key}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{criterion.label}</Typography>
                      <Grid container spacing={1.25}>
                        {criterion.criteria.map((child) => (
                          <Grid size={{ xs: 12, md: 6 }} key={child.key} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <ScoreField criterion={child} value={values[child.key]} previousValue={previousValues[child.key]} allowNoStatement={allowNoStatement} onChange={(value) => setValues((current) => ({ ...current, [child.key]: value }))} />
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  </Grid>
                );
              }
              return (
                <Grid size={{ xs: 12, md: 6 }} key={criterion.key} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <ScoreField criterion={criterion} value={values[criterion.key]} previousValue={previousValues[criterion.key]} allowNoStatement={allowNoStatement} onChange={(value) => setValues((current) => ({ ...current, [criterion.key]: value }))} />
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {onCancel && <Button disabled={saving} onClick={onCancel}>Abbrechen</Button>}
        <Button variant="contained" disabled={!canSave || saving} onClick={handleSave}>
          {saving ? 'Speichert...' : saveLabel}
        </Button>
      </Stack>
    </Stack>
  );
}